/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * FLAC Stream Recorder - Captures audio, encodes to FLAC, and streams chunks for transcription
 *
 * Architecture:
 * - Uses Web Audio API to capture microphone audio
 * - Uses AudioWorklet for high-performance resampling to 16kHz mono
 * - Encodes to FLAC using libflac.js in a Web Worker
 * - Chunks audio at configurable intervals for streaming transcription
 * - POSTs FLAC chunks to transcription endpoint
 * - Aggregates transcripts into a continuous stream
 */

import { FlacEncoder } from './flacEncoder';

export type FlacStreamState =
  | 'idle'
  | 'initializing'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'summarizing'
  | 'error';

export interface TranscriptChunk {
  chunkId: string;
  transcript: string;
  startTime: number; // Start time in seconds within the recording
  endTime: number;   // End time in seconds
  confidence?: number;
}

export interface FlacStreamEventHandlers {
  onStateChange?: (state: FlacStreamState) => void;
  onTranscript?: (chunk: TranscriptChunk, fullTranscript: string) => void;
  onProgress?: (transcript: PartialTranscript) => void;
  onChunkSent?: (chunkId: string, chunkDuration: number) => void;
  onError?: (error: Error) => void;
  onDurationUpdate?: (duration: number) => void;
}

export interface PartialTranscript {
  output: string;
  rawOutput?: string;
  editedOutput?: string;
  duration: number;
}

export interface FlacStreamOptions {
  deviceId?: string;
  transcriptionEndpoint?: string;
  chunkDurationMs?: number;   // Target chunk duration in ms (default: 55000 = 55s)
  sampleRate?: number;        // Target sample rate (default: 16000)
  sessionId?: string;         // Optional session ID for correlation
  authToken?: string;         // Optional auth token for API calls
}

const DEFAULT_OPTIONS: Required<FlacStreamOptions> = {
  deviceId: '',
  transcriptionEndpoint: '/api/private/transcribe',
  chunkDurationMs: 5000,     // 10 seconds for more frequent incremental updates
  sampleRate: 16000,          // 16 kHz for speech recognition
  sessionId: '',
  authToken: ''
};

// Path to AudioWorklet processor module (served from static/wasm)
const RESAMPLER_WORKLET_PATH = '/wasm/audio-resampler-processor.js';

/**
 * FlacStreamRecorder - Records audio, encodes to FLAC, and streams for transcription
 */
export class FlacStreamRecorder {
  private state: FlacStreamState = 'idle';
  private options: Required<FlacStreamOptions>;
  private handlers: FlacStreamEventHandlers = {};

  // Audio capture
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private resamplerNode: AudioWorkletNode | null = null;

  // FLAC encoding
  private flacWorker: Worker | null = null;
  private workerReady = false;
  private allRecordedSamples: Float32Array[] = [];

  // Timing
  private startTime = 0;
  private chunkStartTime = 0;
  private chunkIndex = 0;
  private totalDuration = 0;
  private durationInterval: ReturnType<typeof setInterval> | null = null;

  // Transcript aggregation
  private transcriptChunks: TranscriptChunk[] = [];
  private fullTranscript = '';

  // Pending chunk queue (for orderly processing)
  private pendingChunks: Map<string, Promise<TranscriptChunk | null>> = new Map();

  constructor(options: FlacStreamOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    if (!this.options.sessionId) {
      this.options.sessionId = crypto.randomUUID();
    }
  }

  /**
   * Set event handlers
   */
  setHandlers(handlers: FlacStreamEventHandlers): void {
    this.handlers = handlers;
  }

  /**
   * Get current state
   */
  getState(): FlacStreamState {
    return this.state;
  }

  /**
   * Get full transcript
   */
  getFullTranscript(): string {
    return this.fullTranscript;
  }

  /**
   * Get all transcript chunks
   */
  getTranscriptChunks(): TranscriptChunk[] {
    return [...this.transcriptChunks];
  }

  /**
   * Get current recording duration in seconds
   */
  getDuration(): number {
    return this.totalDuration;
  }

  private setState(newState: FlacStreamState): void {
    this.state = newState;
    this.handlers.onStateChange?.(newState);
  }

  /**
   * Start recording
   */
  async start(deviceId?: string): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error(`Cannot start: current state is ${this.state}`);
    }

    this.setState('initializing');

    try {
      // Request microphone access
      const constraints: MediaStreamConstraints = {
        audio: deviceId ? { deviceId: { ideal: deviceId } } : true
      };

      console.log('[FlacStreamRecorder] Requesting microphone with constraints:', JSON.stringify(constraints));
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[FlacStreamRecorder] Microphone access granted');

      // Create AudioContext - always use native sample rate and resample ourselves
      // This is more reliable than requesting a specific sample rate, which browsers
      // may silently ignore while still reporting the requested rate
      this.audioContext = new AudioContext();
      console.log(
        `[FlacStreamRecorder] Created AudioContext at native ${this.audioContext.sampleRate}Hz, ` +
        `will resample to ${this.options.sampleRate}Hz for FLAC encoding`
      );

      // Create source node from media stream
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Split into individual channels and merge back to mono to ensure 16kHz mono output
      const splitter = this.audioContext.createChannelSplitter(2);
      const merger = this.audioContext.createChannelMerger(1);

      this.sourceNode.connect(splitter);
      splitter.connect(merger, 0, 0); // Left channel
      splitter.connect(merger, 1, 0); // Right channel (sums with Left)

      // Initialize FLAC worker
      this.flacWorker = new Worker(new URL('./flacWorker.ts', import.meta.url), {
        type: 'module' // Restore to module to fix SyntaxError with ESM imports
      });

      const workerInitialized = new Promise<void>((resolve, reject) => {
        const checkInit = (event: MessageEvent) => {
          if (event.data.type === 'initialized') {
            this.flacWorker?.removeEventListener('message', checkInit);
            this.workerReady = true;
            resolve();
          } else if (event.data.success === false) {
            this.flacWorker?.removeEventListener('message', checkInit);
            reject(new Error(event.data.error || 'Worker initialization failed'));
          }
        };
        this.flacWorker?.addEventListener('message', checkInit);
      });

      this.flacWorker.onmessage = (event) => {
        this.handleWorkerMessage(event);
      };

      this.flacWorker.postMessage({
        type: 'init',
        options: {
          sampleRate: 16000, // Target 16kHz for FLAC encoding (speech recognition)
          channels: 1,
          bitsPerSample: 16,
          compression: 5
        }
      });

      // Wait for worker to be ready before starting audio capture
      await workerInitialized;

      // Load and create AudioWorklet for high-performance resampling
      await this.audioContext.audioWorklet.addModule(RESAMPLER_WORKLET_PATH);

      this.resamplerNode = new AudioWorkletNode(this.audioContext, 'resampler-processor', {
        processorOptions: {
          inputRate: this.audioContext.sampleRate,
          outputRate: this.options.sampleRate
        }
      });

      // Handle resampled PCM data from the worklet
      let sampleCounter = 0;
      this.resamplerNode.port.onmessage = (event) => {
        if (event.data.type === 'pcm' && event.data.samples) {
          sampleCounter++;
          // if (sampleCounter === 1 || sampleCounter % 100 === 0) {
          //   console.log(`[FlacStreamRecorder] Received resampled chunk #${sampleCounter}: ${event.data.samples.length} samples at ${event.data.sampleRate || 'unknown'}Hz`);
          // }
          this.handleResampledAudio(event.data.samples);
        }
      };

      // Connect: merger -> resampler worklet
      // Note: We don't connect to destination since we're just processing, not playing back
      merger.connect(this.resamplerNode);

      console.log(`[FlacStreamRecorder] AudioWorklet resampler initialized: ${this.audioContext.sampleRate}Hz -> ${this.options.sampleRate}Hz`);

      // Initialize timing
      this.startTime = Date.now();
      this.chunkStartTime = 0;
      this.chunkIndex = 0;
      this.totalDuration = 0;
      this.transcriptChunks = [];
      this.fullTranscript = '';

      // Start duration update interval
      this.startDurationInterval();

      this.setState('recording');
      console.log('[FlacStreamRecorder] Recording started');

    } catch (error) {
      this.cleanup();
      this.setState('idle');
      const err = error instanceof Error ? error : new Error(String(error));
      this.handlers.onError?.(err);
      throw err;
    }
  }

  /**
   * Handle messages from the FLAC worker
   */
  private handleWorkerMessage(event: MessageEvent): void {
    const { type, buffer, error, isFinal } = event.data;

    if (error) {
      console.error('[FlacStreamRecorder] Worker error:', error);
      this.handlers.onError?.(new Error(error));
      return;
    }

    if (type === 'flacChunk') {
      this.processFlacChunk(buffer, isFinal);
    }
  }

  /**
   * Process a FLAC chunk from the worker
   */
  private async processFlacChunk(buffer: ArrayBuffer, isFinal?: boolean): Promise<void> {
    const flacBlob = new Blob([buffer], { type: 'audio/flac' });
    
    // We don't have exact duration from the worker here easily, 
    // but we know we chunk every ~55s. 
    // For simplicity, we'll estimate based on samples sent or just use the blob.
    // In the new architecture, the worker flushes at ~55s.
    
    const chunkId = `chunk-${this.chunkIndex}`;
    const chunkStartTime = this.chunkStartTime;
    
    // We can't perfectly know the duration without the sample count from worker,
    // but we can estimate or just use the current total duration.
    const currentDuration = this.totalDuration;
    const chunkDuration = currentDuration - chunkStartTime;
    const chunkEndTime = currentDuration;

    console.log(
      `[FlacStreamRecorder] Received chunk ${chunkId}: ${chunkDuration.toFixed(2)}s, ` +
      `${flacBlob.size} bytes, ${chunkStartTime.toFixed(2)}s - ${chunkEndTime.toFixed(2)}s`
    );

    // Notify chunk sent
    this.handlers.onChunkSent?.(chunkId, chunkDuration);

    // Send for transcription
    const transcriptionPromise = this.sendForTranscription(flacBlob, chunkId, chunkStartTime, chunkEndTime);
    this.pendingChunks.set(chunkId, transcriptionPromise);

    transcriptionPromise.then((chunk) => {
      if (chunk) {
        this.transcriptChunks.push(chunk);
        this.transcriptChunks.sort((a, b) => a.startTime - b.startTime);
        this.fullTranscript = this.transcriptChunks.map((c) => c.transcript).join('\n');
        
        this.handlers.onTranscript?.(chunk, this.fullTranscript);
        
        // Also send progress event
        this.handlers.onProgress?.({
          output: this.fullTranscript,
          duration: chunkEndTime
        });
      }
      this.pendingChunks.delete(chunkId);
    }).catch((error) => {
      console.error(`[FlacStreamRecorder] Error processing chunk ${chunkId}:`, error);
      this.pendingChunks.delete(chunkId);
    });

    // Update state for next chunk
    this.chunkStartTime = chunkEndTime;
    this.chunkIndex++;
  }

  /**
   * Handle resampled audio data from the AudioWorklet
   * This receives 16kHz mono samples ready for FLAC encoding
   */
  private handleResampledAudio(samples: Float32Array): void {
    if (this.state !== 'recording') return;

    // Store resampled samples for full audio blob
    this.allRecordedSamples.push(new Float32Array(samples));

    // Send already-resampled 16kHz samples directly to FLAC worker
    this.postToWorker(samples);
  }

  /**
   * Post PCM samples to the FLAC worker
   * Samples are already resampled to 16kHz by the AudioWorklet
   */
  private postToWorker(samples: Float32Array): void {
    if (!this.flacWorker || !this.workerReady) return;

    // Create a copy for transfer to the worker
    const samplesCopy = new Float32Array(samples);

    this.flacWorker.postMessage({
      type: 'pcm',
      samples: samplesCopy
    }, [samplesCopy.buffer]);
  }

  /**
   * Send FLAC chunk to transcription endpoint
   */
  private async sendForTranscription(
    flacBlob: Blob,
    chunkId: string,
    startTime: number,
    endTime: number
  ): Promise<TranscriptChunk | null> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'audio/flac',
        'X-Chunk-Id': chunkId,
        'X-Session-Id': this.options.sessionId,
        'X-Chunk-Start': startTime.toString(),
        'X-Chunk-End': endTime.toString()
      };

      if (this.options.authToken) {
        headers['Authorization'] = `Bearer ${this.options.authToken}`;
      }

      const response = await fetch(this.options.transcriptionEndpoint, {
        method: 'POST',
        headers,
        body: flacBlob
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Transcription failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      return {
        chunkId,
        transcript: result.transcript || '',
        startTime,
        endTime,
        confidence: result.confidence
      };

    } catch (error) {
      console.error(`[FlacStreamRecorder] Transcription error for ${chunkId}:`, error);
      this.handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * Stop recording
   */
  async stop(): Promise<{ transcript: string; chunks: TranscriptChunk[]; audioBlob?: Blob }> {
    if (this.state !== 'recording' && this.state !== 'paused') {
      console.warn(`[FlacStreamRecorder] Cannot stop: current state is ${this.state}`);
      return { transcript: this.fullTranscript, chunks: this.transcriptChunks };
    }

    this.setState('stopping');

    // Finalize worker and wait for last chunk
    if (this.flacWorker) {
      const workerDone = new Promise<void>((resolve) => {
        const checkDone = (event: MessageEvent) => {
          if (event.data.type === 'done' || (event.data.type === 'flacChunk' && event.data.isFinal)) {
            this.flacWorker?.removeEventListener('message', checkDone);
            // Give a tiny bit of time for processFlacChunk to be called if it was a flacChunk
            setTimeout(resolve, 10);
          }
        };
        this.flacWorker?.addEventListener('message', checkDone);
      });

      this.flacWorker.postMessage({ type: 'finish' });
      await workerDone;
    }

    // Wait for all pending transcriptions
    if (this.pendingChunks.size > 0) {
      console.log(`[FlacStreamRecorder] Waiting for ${this.pendingChunks.size} pending transcriptions...`);
      await Promise.all(Array.from(this.pendingChunks.values()));
    }

    // Create full audio blob (FLAC)
    let audioBlob: Blob | undefined;
    if (this.allRecordedSamples.length > 0) {
      try {
        // Concatenate all samples
        const totalLength = this.allRecordedSamples.reduce((sum, arr) => sum + arr.length, 0);
        const allSamples = new Float32Array(totalLength);
        let offset = 0;
        for (const arr of this.allRecordedSamples) {
          allSamples.set(arr, offset);
          offset += arr.length;
        }

        // Use worker for encoding to keep UI responsive
        audioBlob = await this.encodeFullFlacWithWorker(allSamples);
      } catch (error) {
        console.error('[FlacStreamRecorder] Failed to create full FLAC blob:', error);
      }
    }

    this.cleanup();
    this.setState('idle');

    console.log(`[FlacStreamRecorder] Recording stopped. Total duration: ${this.totalDuration.toFixed(2)}s`);

    return {
      transcript: this.fullTranscript,
      chunks: this.transcriptChunks,
      audioBlob
    };
  }

  /**
   * Encode full recording to FLAC using a Web Worker
   */
  private async encodeFullFlacWithWorker(samples: Float32Array): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const worker = new Worker(new URL('./flacWorker.ts', import.meta.url), {
          type: 'module'
        });

        worker.onmessage = (event) => {
          const { success, blob, error } = event.data;
          if (success) {
            resolve(blob);
          } else {
            reject(new Error(error || 'Unknown worker error'));
          }
          worker.terminate();
        };

        worker.onerror = (error) => {
          reject(error);
          worker.terminate();
        };

        worker.postMessage(
          {
            samples,
            options: {
              sampleRate: this.options.sampleRate,
              channels: 1,
              bitsPerSample: 16,
              compression: 5
            }
          },
          [samples.buffer] // Transfer the ArrayBuffer to avoid copying
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.state !== 'recording') {
      console.warn(`[FlacStreamRecorder] Cannot pause: current state is ${this.state}`);
      return;
    }

    // Disconnect resampler to stop receiving audio
    this.resamplerNode?.disconnect();
    this.stopDurationInterval();
    this.setState('paused');
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.state !== 'paused') {
      console.warn(`[FlacStreamRecorder] Cannot resume: current state is ${this.state}`);
      return;
    }

    // Reconnect resampler - note: we need to reconnect to the source
    // This is a limitation; for proper pause/resume we'd need to track the merger node
    console.warn('[FlacStreamRecorder] Resume after pause may require re-initialization');
    this.startDurationInterval();
    this.setState('recording');
  }

  /**
   * Abort recording without processing
   */
  abort(): void {
    if (this.state === 'idle') return;

    this.cleanup();
    this.transcriptChunks = [];
    this.fullTranscript = '';
    this.setState('idle');
  }

  private startDurationInterval(): void {
    this.stopDurationInterval();
    this.durationInterval = setInterval(() => {
      this.totalDuration = (Date.now() - this.startTime) / 1000;
      this.handlers.onDurationUpdate?.(this.totalDuration);
    }, 1000); // Update once per second instead of every 100ms
  }

  private stopDurationInterval(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private cleanup(): void {
    this.stopDurationInterval();

    // Disconnect and clean up audio nodes
    if (this.resamplerNode) {
      this.resamplerNode.port.onmessage = null;
      this.resamplerNode.disconnect();
      this.resamplerNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(console.error);
      this.audioContext = null;
    }

    // Stop media stream tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Clean up FLAC worker
    if (this.flacWorker) {
      this.flacWorker.terminate();
      this.flacWorker = null;
      this.workerReady = false;
    }

    this.allRecordedSamples = [];
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.abort();
    this.handlers = {};
  }
}
